import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { formatCurrency } from '../utils/currency';
import { getApiUrl } from '../config';
import './Transactions.css';

const Transactions = ({ config }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [allTransactions, setAllTransactions] = useState([]); // For generating month dropdown
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState(searchParams.get('search') || '');
  const [account, setAccount] = useState(searchParams.get('account') || '');
  const [period, setPeriod] = useState(searchParams.get('period') || '');
  const [selectedMonth, setSelectedMonth] = useState(searchParams.get('month') || '');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [expandedTxs, setExpandedTxs] = useState(new Set());

  // Update URL params when filters change
  useEffect(() => {
    const params = {};
    if (filter) params.search = filter;
    if (account) params.account = account;
    if (period) params.period = period;
    if (selectedMonth) params.month = selectedMonth;
    setSearchParams(params, { replace: true });
  }, [filter, account, period, selectedMonth]);

  useEffect(() => {
    loadTransactions();
  }, [config, period, selectedMonth]);

  // Load all transactions once for month dropdown
  useEffect(() => {
    const loadAllTransactions = async () => {
      try {
        const response = await axios.post(getApiUrl('/api/hledger/command'), {
          journalPath: config.journalPath,
          command: 'print --output-format=json'
        });
        const data = JSON.parse(response.data.stdout || '[]');
        setAllTransactions(data);
      } catch (err) {
        console.error('Error loading all transactions for dropdown:', err);
      }
    };

    if (config?.journalPath) {
      loadAllTransactions();
    }
  }, [config]);

  const loadTransactions = async () => {
    setLoading(true);
    setError('');

    try {
      let args = '--output-format=json';

      // Use selectedMonth if set, otherwise use period
      if (selectedMonth) {
        args += ` -p ${selectedMonth}`;
      } else if (period) {
        args += ` -p ${period}`;
      }

      // Use 'print' instead of 'register' to get full transactions with all postings
      const response = await axios.post(getApiUrl('/api/hledger/command'), {
        journalPath: config.journalPath,
        command: `print ${args}`
      });

      const data = JSON.parse(response.data.stdout || '[]');
      setTransactions(data);
      setLoading(false);
    } catch (err) {
      console.error('Error loading transactions:', err);
      setError('Error loading transactions: ' + (err.response?.data?.error || err.message));
      setLoading(false);
    }
  };

  const navigateMonth = (direction) => {
    let year, month;
    if (selectedMonth) {
      const parts = selectedMonth.split('-');
      year = parseInt(parts[0]);
      month = parseInt(parts[1]) - 1; // Convert to 0-indexed
    } else {
      const now = new Date();
      year = now.getFullYear();
      month = now.getMonth();
    }

    // Create date using explicit parameters to avoid timezone issues
    const currentDate = new Date(year, month, 1);
    currentDate.setMonth(currentDate.getMonth() + direction);

    const newYear = currentDate.getFullYear();
    const newMonth = String(currentDate.getMonth() + 1).padStart(2, '0'); // Convert back to 1-indexed
    setSelectedMonth(`${newYear}-${newMonth}`);
    setPeriod(''); // Clear period when using month navigation
  };

  const clearAllFilters = () => {
    setFilter('');
    setAccount('');
    setPeriod('');
    setSelectedMonth('');
    setStatusFilter('all');
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const parseDescription = (description) => {
    if (!description || typeof description !== 'string') return { payee: description, note: null };

    // hledger format: "Payee | Note"
    const parts = description.split(' | ');
    if (parts.length === 1) {
      return { payee: parts[0], note: null };
    }

    return { payee: parts[0], note: parts.slice(1).join(' | ') };
  };

  const formatAccountName = (fullAccount) => {
    if (!fullAccount || typeof fullAccount !== 'string') return fullAccount;

    // Split by colon and remove the top-level (Assets, Expenses, etc.)
    const parts = fullAccount.split(':');
    if (parts.length <= 1) return fullAccount;

    // Remove first part (top-level account type) and return JSX with proper padding
    return parts.slice(1).map((part, idx) => (
      <React.Fragment key={idx}>
        {idx > 0 && <span style={{ padding: '0 8px', color: '#6e7681' }}>›</span>}
        {part}
      </React.Fragment>
    ));
  };

  const getAccountType = (accountName) => {
    if (!accountName || typeof accountName !== 'string') return 'other';
    if (accountName.startsWith('Assets')) return 'asset';
    if (accountName.startsWith('Liabilities')) return 'liability';
    if (accountName.startsWith('Income')) return 'income';
    if (accountName.startsWith('Expenses')) return 'expense';
    if (accountName.startsWith('Equity')) return 'equity';
    return 'other';
  };

  const toggleExpanded = (txIndex) => {
    const newExpanded = new Set(expandedTxs);
    if (newExpanded.has(txIndex)) {
      newExpanded.delete(txIndex);
    } else {
      newExpanded.add(txIndex);
    }
    setExpandedTxs(newExpanded);
  };

  const handleTagClick = (tag, e) => {
    e.stopPropagation(); // Prevent expanding/collapsing the transaction
    const tagStr = typeof tag === 'string' ? tag : String(tag);
    const searchTag = tagStr.replace(',', ':');
    console.log('Tag clicked:', searchTag);
    setFilter(searchTag);
  };

  const handleStatusClick = async (tx, e) => {
    e.stopPropagation(); // Prevent expanding/collapsing the transaction

    // Cycle through statuses: unmarked → Pending → Cleared → unmarked
    let newStatus = '';
    if (!tx.tstatus || tx.tstatus === 'Unmarked') {
      newStatus = 'Pending';
    } else if (tx.tstatus === 'Pending') {
      newStatus = 'Cleared';
    } else if (tx.tstatus === 'Cleared') {
      newStatus = ''; // unmarked
    }

    try {
      await axios.post(getApiUrl('/api/journal/update-status'), {
        journalPath: config.journalPath,
        date: tx.tdate,
        description: tx.tdescription,
        newStatus: newStatus
      });

      // Update status locally without reloading
      setTransactions(prevTransactions =>
        prevTransactions.map(t =>
          t.tdate === tx.tdate && t.tdescription === tx.tdescription
            ? { ...t, tstatus: newStatus || 'Unmarked' }
            : t
        )
      );
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update transaction status: ' + (err.response?.data?.error || err.message));
    }
  };

  const getAllTags = (tx) => {
    // Collect all tags from transaction and postings
    const tags = new Set();
    if (tx.ttags) {
      tx.ttags.forEach(tag => {
        let tagName;
        if (Array.isArray(tag)) {
          tagName = tag[0]; // Get first element
          if (tag[1]) tagName += ':' + tag[1]; // Add value if present
        } else {
          tagName = typeof tag === 'string' ? tag : String(tag);
        }
        if (tagName) tags.add(tagName);
      });
    }
    tx.tpostings.forEach(posting => {
      if (posting.ptags) {
        posting.ptags.forEach(tag => {
          let tagName;
          if (Array.isArray(tag)) {
            tagName = tag[0]; // Get first element
            if (tag[1]) tagName += ':' + tag[1]; // Add value if present
          } else {
            tagName = typeof tag === 'string' ? tag : String(tag);
          }
          if (tagName) tags.add(tagName);
        });
      }
    });
    return Array.from(tags);
  };

  const getTxNetAmount = (tx) => {
    // Calculate net amount (usually one side of the transaction)
    let maxAmount = 0;
    tx.tpostings.forEach(posting => {
      if (posting.pamount && posting.pamount.length > 0) {
        const amt = Math.abs(posting.pamount[0]?.aquantity?.floatingPoint || 0);
        if (amt > maxAmount) maxAmount = amt;
      }
    });
    return maxAmount;
  };

  const filteredAndSortedTransactions = transactions
    .filter(tx => {
      // Apply status filter
      if (statusFilter !== 'all') {
        const txStatus = tx.tstatus || 'Unmarked';
        if (statusFilter === 'unmarked' && txStatus !== 'Unmarked' && txStatus !== '') return false;
        if (statusFilter === 'pending' && txStatus !== 'Pending') return false;
        if (statusFilter === 'cleared' && txStatus !== 'Cleared') return false;
      }

      // Apply account filter
      if (account) {
        const accountLower = account.toLowerCase();
        const hasMatchingAccount = tx.tpostings.some(p =>
          (p.paccount || '').toLowerCase().includes(accountLower)
        );
        if (!hasMatchingAccount) return false;
      }

      // Apply text search filter
      if (!filter) return true;
      const searchLower = filter.toLowerCase();

      // Search in description, comments, account names, tags
      const description = tx.tdescription || '';
      const comment = tx.tcomment || '';
      const accountNames = tx.tpostings.map(p => p.paccount || '').join(' ');
      const tags = getAllTags(tx).join(' ');

      return (
        description.toLowerCase().includes(searchLower) ||
        comment.toLowerCase().includes(searchLower) ||
        accountNames.toLowerCase().includes(searchLower) ||
        tags.toLowerCase().includes(searchLower)
      );
    })
    .sort((a, b) => {
      let compareValue = 0;

      switch (sortBy) {
        case 'date':
          compareValue = a.tdate.localeCompare(b.tdate);
          break;
        case 'description':
          compareValue = (a.tdescription || '').localeCompare(b.tdescription || '');
          break;
        case 'amount':
          compareValue = getTxNetAmount(a) - getTxNetAmount(b);
          break;
        default:
          compareValue = 0;
      }

      return sortOrder === 'asc' ? compareValue : -compareValue;
    });

  if (loading) {
    return (
      <div className="page-loading">
        <div className="spinner"></div>
        <p>Loading transactions...</p>
      </div>
    );
  }

  return (
    <div className="transactions-page">
      <div className="page-header">
        <h1>Transactions</h1>
        <p>View and search all journal entries</p>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Filter Transactions</h2>
        </div>
        <div className="card-body">
          <div className="filter-grid">
            <div className="form-group">
              <label className="form-label">Search</label>
              <input
                type="text"
                className="form-input"
                placeholder="Search description, account, tags..."
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Account</label>
              <input
                type="text"
                className="form-input"
                placeholder="e.g., expenses:food"
                value={account}
                onChange={(e) => setAccount(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Period / Month</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <select
                  className="form-select"
                  value={period}
                  onChange={(e) => {
                    setPeriod(e.target.value);
                    if (e.target.value) setSelectedMonth(''); // Clear month when selecting period
                  }}
                  style={{ flex: '0 0 140px' }}
                >
                  <option value="">Period</option>
                  <option value="thismonth">This month</option>
                  <option value="lastmonth">Last month</option>
                  <option value="thisquarter">This quarter</option>
                  <option value="thisyear">This year</option>
                  <option value="lastyear">Last year</option>
                </select>
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flex: 1 }}>
                  <button
                    className="btn btn-sm"
                    onClick={() => navigateMonth(-1)}
                    title="Previous month"
                    style={{ padding: '0.25rem 0.5rem' }}
                  >
                    ◀
                  </button>
                  <select
                    className="form-select"
                    value={selectedMonth}
                    onChange={(e) => {
                      setSelectedMonth(e.target.value);
                      setPeriod(''); // Clear period when selecting month
                    }}
                    style={{ flex: 1 }}
                  >
                    <option value="">Select month...</option>
                    {(() => {
                      const months = [];
                      const now = new Date();

                      // Find earliest and latest transaction dates from ALL transactions
                      let minDate = now;
                      let maxDate = now;

                      if (allTransactions.length > 0) {
                        allTransactions.forEach(tx => {
                          if (tx.tdate) {
                            const txDate = new Date(tx.tdate);
                            if (txDate < minDate) minDate = txDate;
                            if (txDate > maxDate) maxDate = txDate;
                          }
                        });
                      }

                      // Use current month as max if it's later than latest transaction
                      if (now > maxDate) maxDate = now;

                      // Generate options from latest month down to earliest transaction month
                      // Newest first
                      const startYear = maxDate.getFullYear();
                      const startMonth = maxDate.getMonth();
                      const endYear = minDate.getFullYear();
                      const endMonth = minDate.getMonth();

                      let currentYear = startYear;
                      let currentMonth = startMonth;

                      while (currentYear > endYear || (currentYear === endYear && currentMonth >= endMonth)) {
                        const year = currentYear;
                        const month = String(currentMonth + 1).padStart(2, '0');
                        const value = `${year}-${month}`;
                        const date = new Date(year, currentMonth, 1);
                        const label = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
                        months.push(<option key={value} value={value}>{label}</option>);

                        // Move to previous month
                        currentMonth--;
                        if (currentMonth < 0) {
                          currentMonth = 11;
                          currentYear--;
                        }
                      }

                      return months;
                    })()}
                  </select>
                  <button
                    className="btn btn-sm"
                    onClick={() => navigateMonth(1)}
                    title="Next month"
                    style={{ padding: '0.25rem 0.5rem' }}
                  >
                    ▶
                  </button>
                  {selectedMonth && (
                    <button
                      className="btn btn-sm"
                      onClick={() => setSelectedMonth('')}
                      title="Clear month filter"
                      style={{ padding: '0.25rem 0.5rem' }}
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All</option>
                <option value="unmarked">Unmarked</option>
                <option value="pending">Pending</option>
                <option value="cleared">Cleared</option>
              </select>
            </div>
          </div>
          <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
            <button
              className="btn btn-secondary"
              onClick={clearAllFilters}
              disabled={!filter && !account && !period && !selectedMonth && statusFilter === 'all'}
            >
              Clear All Filters
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">
            {filteredAndSortedTransactions.length} Transaction{filteredAndSortedTransactions.length !== 1 ? 's' : ''}
          </h2>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <span style={{ fontSize: '12px', color: '#8b949e' }}>Sort by:</span>
            <button
              className={`btn btn-sm ${sortBy === 'date' ? 'btn-primary' : ''}`}
              onClick={() => handleSort('date')}
            >
              Date {sortBy === 'date' && (sortOrder === 'asc' ? '↑' : '↓')}
            </button>
            <button
              className={`btn btn-sm ${sortBy === 'description' ? 'btn-primary' : ''}`}
              onClick={() => handleSort('description')}
            >
              Description {sortBy === 'description' && (sortOrder === 'asc' ? '↑' : '↓')}
            </button>
            <button
              className={`btn btn-sm ${sortBy === 'amount' ? 'btn-primary' : ''}`}
              onClick={() => handleSort('amount')}
            >
              Amount {sortBy === 'amount' && (sortOrder === 'asc' ? '↑' : '↓')}
            </button>
            <button className="btn btn-sm" onClick={loadTransactions}>
              Refresh
            </button>
          </div>
        </div>
        <div className="card-body">
          {filteredAndSortedTransactions.length === 0 ? (
            <div className="empty-state">
              <p>No transactions found</p>
            </div>
          ) : (
            <div className="transactions-list">
              {filteredAndSortedTransactions.map((tx, index) => {
                const isExpanded = expandedTxs.has(index);
                const netAmount = getTxNetAmount(tx);
                const statusIcon = tx.tstatus === 'Cleared' ? '✓' : tx.tstatus === 'Pending' ? '!' : '';
                const allTags = getAllTags(tx);
                // Get order number from hledger's tcode field (transaction code)
                const orderNumber = tx.tcode || null;
                const { payee, note } = parseDescription(tx.tdescription);

                return (
                  <div key={index} className={`transaction-card ${isExpanded ? 'expanded' : ''}`}>
                    {/* Transaction Header */}
                    <div
                      className="transaction-header"
                      onClick={() => toggleExpanded(index)}
                      style={{ cursor: 'pointer', padding: '8px 12px', borderBottom: '1px solid #30363d', display: 'flex', alignItems: 'center', gap: '12px', fontSize: '13px' }}
                    >
                      <span className="text-mono" style={{ color: '#8b949e', minWidth: '80px' }}>{tx.tdate}</span>
                      <span style={{ flex: 1 }}>
                        <span style={{ color: '#e6edf3', fontWeight: 500 }}>{payee}</span>
                        {note && <span style={{ color: '#6e7681', marginLeft: '12px' }}>{note}</span>}
                      </span>
                      {allTags.length > 0 && (
                        <span style={{ fontSize: '11px', color: '#79c0ff', display: 'flex', gap: '6px' }}>
                          {allTags.map((tag, tagIdx) => {
                            const tagStr = typeof tag === 'string' ? tag : String(tag);
                            return (
                              <span
                                key={tagIdx}
                                onClick={(e) => handleTagClick(tag, e)}
                                style={{
                                  cursor: 'pointer',
                                  padding: '2px 4px',
                                  borderRadius: '3px',
                                  transition: 'background-color 0.2s'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(121, 192, 255, 0.2)'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                title={`Filter by ${tagStr.replace(',', ':')}`}
                              >
                                #{tagStr.replace(',', ':')}
                              </span>
                            );
                          })}
                        </span>
                      )}
                      <span
                        onClick={(e) => handleStatusClick(tx, e)}
                        style={{
                          minWidth: '20px',
                          textAlign: 'center',
                          cursor: 'pointer',
                          color: tx.tstatus === 'Cleared' ? '#3fb950' : tx.tstatus === 'Pending' ? '#d29922' : '#6e7681',
                          fontSize: '14px',
                          padding: '2px 4px',
                          borderRadius: '3px',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        title={`Status: ${tx.tstatus || 'Unmarked'} (click to change)`}
                      >
                        {statusIcon || '○'}
                      </span>
                      <span className="text-mono" style={{ minWidth: '100px', textAlign: 'right' }}>{formatCurrency(netAmount)}</span>
                      <span style={{ color: '#8b949e' }}>{isExpanded ? '▲' : '▼'}</span>
                    </div>

                    {/* Transaction Details (when expanded) */}
                    {isExpanded && (
                      <div className="transaction-details" style={{ padding: '8px 12px', backgroundColor: '#0d1117', fontSize: '12px' }}>
                        {/* Order Number */}
                        {orderNumber && (
                          <div style={{ marginBottom: '8px', fontSize: '11px', color: '#8b949e' }}>
                            <span style={{ color: '#6e7681' }}>Order: </span>
                            <span className="text-mono" style={{ color: '#a371f7' }}>{orderNumber}</span>
                          </div>
                        )}

                        {/* Transaction Comment - only show if it's not just tags */}
                        {tx.tcomment && (() => {
                          let cleanTxComment = tx.tcomment.trim();
                          // Remove tag patterns from comment (tags are shown in header)
                          if (tx.ttags && tx.ttags.length > 0) {
                            tx.ttags.forEach(tag => {
                              // Tags in comments use : but are parsed as , in ptags
                              // e.g., "MarciaBirthday:2024" becomes tag "MarciaBirthday,2024"
                              const tagStr = typeof tag === 'string' ? tag : String(tag);
                              const tagPattern = tagStr.replace(',', ':');

                              // Remove both "TagName:" and "#TagName:" patterns
                              cleanTxComment = cleanTxComment.replace(new RegExp(`\\b${tagPattern}\\b`, 'g'), '');
                              cleanTxComment = cleanTxComment.replace(new RegExp(`#${tagPattern}`, 'g'), '');
                            });
                            cleanTxComment = cleanTxComment.replace(/^[;,:\s]+|[;,:\s]+$/g, '').trim();
                          }

                          return cleanTxComment && (
                            <div style={{ marginBottom: '8px', color: '#8b949e', fontStyle: 'italic' }}>
                              ; {cleanTxComment}
                            </div>
                          );
                        })()}

                        {/* Postings */}
                        {tx.tpostings.map((posting, pIdx) => {
                          const accountType = getAccountType(posting.paccount);
                          const amount = posting.pamount && posting.pamount.length > 0
                            ? posting.pamount[0]?.aquantity?.floatingPoint || 0
                            : 0;
                          const commodity = posting.pamount && posting.pamount.length > 0
                            ? posting.pamount[0]?.acommodity || ''
                            : '';

                          // Clean comment: remove tag syntax if tags are parsed separately
                          let cleanComment = posting.pcomment || '';
                          if (posting.ptags && posting.ptags.length > 0) {
                            posting.ptags.forEach(tag => {
                              // Tags can be arrays like ["FinnBirthday", "2024"] or strings
                              let fullTag;

                              if (Array.isArray(tag)) {
                                // Build full tag: "FinnBirthday:2024" or just "FinnBirthday" if no value
                                if (tag[1]) {
                                  fullTag = `${tag[0]}:${tag[1]}`;
                                } else {
                                  fullTag = tag[0];
                                }
                              } else {
                                fullTag = typeof tag === 'string' ? tag : String(tag);
                                fullTag = fullTag.replace(',', ':');
                              }

                              if (!fullTag) return;

                              // Remove the complete tag pattern (with or without #)
                              cleanComment = cleanComment.replace(new RegExp(`${fullTag}`, 'g'), '');
                              cleanComment = cleanComment.replace(new RegExp(`#${fullTag}`, 'g'), '');
                            });
                            cleanComment = cleanComment.replace(/^[;,:\s]+|[;,:\s]+$/g, '').trim();
                          }

                          return (
                            <div key={pIdx} style={{ marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <span className={`badge badge-${accountType}`} style={{ fontSize: '10px', padding: '2px 6px', minWidth: '60px', textAlign: 'center' }}>
                                {accountType}
                              </span>
                              <span style={{ flex: 1, color: '#c9d1d9' }}>
                                {formatAccountName(posting.paccount)}
                              </span>
                              {cleanComment && (
                                <span style={{ color: '#8b949e', fontSize: '11px' }}>; {cleanComment}</span>
                              )}
                              {posting.ptags && posting.ptags.length > 0 && (
                                <span style={{ fontSize: '10px', color: '#79c0ff', display: 'flex', gap: '4px' }}>
                                  {posting.ptags.map((tag, tagIdx) => {
                                    const tagStr = typeof tag === 'string' ? tag : String(tag);
                                    return (
                                      <span
                                        key={tagIdx}
                                        onClick={(e) => handleTagClick(tag, e)}
                                        style={{
                                          cursor: 'pointer',
                                          padding: '2px 4px',
                                          borderRadius: '3px',
                                          transition: 'background-color 0.2s'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(121, 192, 255, 0.2)'}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                        title={`Filter by ${tagStr.replace(',', ':')}`}
                                      >
                                        #{tagStr.replace(',', ':')}
                                      </span>
                                    );
                                  })}
                                </span>
                              )}
                              {amount !== 0 && (
                                <span className="text-mono" style={{ minWidth: '100px', textAlign: 'right', color: amount > 0 ? '#3fb950' : '#f85149' }}>
                                  {formatCurrency(amount, commodity || '$')}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Transactions;
